import AppConfig from '@app';
import CdnBalancer from 'cdn-balancer-plugin';
import Channel from '../../shared/classes/channel';
import DeviceConfig from '@devices/config';
import FastEvent from '../../shared/classes/fastEvent';
import PlayerUtils from '../../shared/utils/player';
import { PlayerType } from '../../shared/types/enums/player';
import { store } from '@shared/store/store';

// Plugins & Players
import '../../plugins/dashjs/dash.all.min-es5';
import '../../plugins/hlsjs/hls.min-es5';
import Shaka from 'shaka-player/dist/shaka-player.compiled';
const youboraLib = require('youboralib');
require('youbora-adapter-dashjs');
require('youbora-adapter-hlsjs');
require('youbora-adapter-shaka');

interface Parameters {
  asset?: FastEvent;
  channel?: Channel;
  player: {
    instance: dashjs.MediaPlayerClass | Hls | Shaka.Player;
    type: PlayerType;
  };
  callback?: () => void;
}

class Youbora {
  plugin: typeof youboraLib | null;
  adapter: typeof youboraLib.getAdapter | null;
  balancer?: CdnBalancer | null;

  asset?: FastEvent;
  channel?: Channel;
  type?: PlayerType;

  constructor({ asset, channel, player, callback }: Parameters) {
    try {
      // Set variables
      this.asset = asset;
      this.channel = channel;
      this.type = player.type;

      // Create unified plugin instance
      if (player?.type !== PlayerType.AVPLAYER) {
        this.plugin = new youboraLib.Plugin({
          accountCode: AppConfig.youboraCode,
        });
      }

      // Get adapter options
      const options = this.getAnalyticsOptions();
      if (player?.type !== PlayerType.AVPLAYER) {
        this.plugin.setOptions(options);
      }
      // Set player adapter
      switch (player.type) {
        case PlayerType.DASHJS:
          this.plugin.setAdapter(
            new youboraLib.adapters.DashJS(player.instance)
          );
          this.adapter = this.plugin.getAdapter();
          break;
        case PlayerType.HLSJS:
          this.plugin.setAdapter(
            new youboraLib.adapters.Hlsjs(player.instance)
          );
          this.adapter = this.plugin.getAdapter();
          break;
        case PlayerType.SHAKA:
          this.plugin.setAdapter(
            new youboraLib.adapters.Shaka(player.instance)
          );
          this.adapter = this.plugin.getAdapter();
          break;
        case PlayerType.AVPLAYER:
          this.plugin = new youbora.Plugin({
            accountCode: AppConfig.youboraCode,
          });
          if (typeof youbora !== undefined) {
            this.plugin.setOptions(options);
            this.plugin.setAdapter(
              new youbora.adapters.SamsungInfolink('avPlayer')
            );
            this.adapter = this.plugin.getAdapter();
            this.adapter.playerType = 'AvPlayer';
            this.adapter.fireStart();
            break;
          }
      }

      // Create balancer instance
      const { user } = store.getState();
      const deviceActive = DeviceConfig.capabilities.balancer;
      const operatorActive =
        user.operator?.cdnBalancer?.active &&
        user.operator?.cdnBalancer?.platforms.includes(DeviceConfig.info.make); // Check whether balancer is active at operator level or not
      // Check active and platform
      const { active: contentActive } =
        channel?.cdnBalancer?.live || asset?.channel?.cdnBalancer?.vod || {}; // Check whether balancer is active at channel/vod level or not

      if (deviceActive && operatorActive && contentActive) {
        this.balancer = new CdnBalancer(AppConfig.youboraCode, {
          legacyPlugin: this.plugin,
        });
        const balancerOptions = this.getBalancerOptions();
        this.balancer?.setBalancerOptions(balancerOptions);
        this.balancer?.setLogLevel(6);
      }
    } catch (e) {
      // Destroy plugin on initialization error
      console.error('[Youbora] Could not initialize youbora plugin', e);
      this.plugin = null;
    } finally {
      // Trigger callback on initialization end
      if (callback) {
        callback();
      }
    }
  }

  destroy() {
    try {
      if (this.balancer) {
        this.balancer.destroy();
        this.balancer = null;
      }
    } catch (e) {
      console.error('[Youbora] Could not destroy balancer instance', e);
    }

    try {
      if (this.adapter) {
        this.adapter?.fireStop();
      } else {
        this.plugin?.getAdapter()?.fireStop();
      }
      this.plugin?.removeAdapter();
      this.plugin = null;
    } catch (e) {
      console.error('[Youbora] Could not destroy youbora plugin', e);
    }
  }

  start() {
    try {
      if (this.adapter) {
        this.adapter?.fireStart();
      } else {
        this.plugin?.getAdapter()?.fireStart();
      }
    } catch (e) {
      console.error('[Youbora] Could not start youbora plugin', e);
    }
  }

  getAdapter() {
    return this.plugin?.getAdapter();
  }

  getAnalyticsOptions() {
    let options = {};
    if (this.asset) {
      options = PlayerUtils.getPlayerMetadata(this.asset);
    } else if (this.channel) {
      options = PlayerUtils.getZapperMetadata(this.channel);
    }
    return options;
  }

  getBalancerOptions() {
    let options = {};
    if (this.asset || (this.channel && this.balancer)) {
      options = PlayerUtils.getBalancerOptions(
        this.channel || this.asset?.channel,
        !!this.channel
      );
    }
    return options;
  }

  updateOptions() {
    const options = this.getAnalyticsOptions();
    this.plugin.setOptions(options);
  }
}

export default Youbora;
